import { Box, Drawer, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";
import {
  CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { AXIS_TEXT, GRID, SERIES } from "../theme";
import { Timeline } from "../types";
import { useStats } from "../useStats";
import StatsGate from "./StatsGate";
import { Empty } from "./Card";

/**
 * Fills in any minute missing between the first and last minute seen, at
 * zero counts, so a straight line between two far-apart minutes never reads
 * as gradual activity across the minutes the AL sent nothing for. Period and
 * score stay null on a filled minute: neither is known for it.
 */
function zeroFillMinutes(perMinute: Timeline["perMinute"]): Timeline["perMinute"] {
  if (perMinute.length === 0) return perMinute;
  const byMinute = new Map(perMinute.map((p) => [p.minute, p]));
  const minutes = perMinute.map((p) => p.minute);
  const first = Math.min(...minutes);
  const last = Math.max(...minutes);
  const filled: Timeline["perMinute"] = [];
  for (let minute = first; minute <= last; minute += 1) {
    filled.push(byMinute.get(minute) ?? {
      minute, messages: 0, quickReactions: 0, messageReactions: 0,
      period: null, scoreHome: null, scoreAway: null,
    });
  }
  return filled;
}

function TimelineBody({ tenant, gameId, reloadKey }: { tenant: string; gameId: string; reloadKey: number }) {
  const state = useStats<Timeline>(`/games/${encodeURIComponent(gameId)}/timeline`, `tenant=${encodeURIComponent(tenant)}`, reloadKey);
  const tick = { fill: AXIS_TEXT, fontSize: 12 };
  return (
    <StatsGate state={state}>
      {(data) => {
        if (data.perMinute.length === 0) return <Empty />;
        const perMinute = zeroFillMinutes(data.perMinute);
        // Mark where a new period starts, so bursts can be read against the game.
        const periodStarts = perMinute.filter((p, i) =>
          p.period !== null && perMinute.slice(0, i).every((q) => q.period === null || q.period < p.period!));
        return (
          <>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={perMinute} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis dataKey="minute" type="number" domain={["dataMin", "dataMax"]} tick={tick} stroke={GRID}
                  label={{ value: "Minute of playback", position: "insideBottom", offset: -2, fill: AXIS_TEXT, fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={tick} stroke={GRID} width={40} />
                <Tooltip
                  labelFormatter={(m) => {
                    const p = perMinute.find((x) => x.minute === Number(m));
                    const score = p && p.scoreHome !== null && p.scoreAway !== null ? `, ${p.scoreHome}-${p.scoreAway}` : "";
                    return `Minute ${m}${p?.period ? `, period ${p.period}` : ""}${score}`;
                  }}
                  itemStyle={{ color: AXIS_TEXT }}
                />
                <Legend formatter={(value) => <span style={{ color: AXIS_TEXT }}>{value}</span>} />
                {periodStarts.map((p) => (
                  <ReferenceLine key={p.minute} x={p.minute} stroke={AXIS_TEXT} strokeDasharray="3 3"
                    label={{ value: `P${p.period}`, position: "top", fill: AXIS_TEXT, fontSize: 12 }} />
                ))}
                <Line dataKey="messages" name="Messages" stroke={SERIES[0]} strokeWidth={2} dot={false} isAnimationActive={false} />
                <Line dataKey="quickReactions" name="Quick reactions" stroke={SERIES[1]} strokeWidth={2} dot={false} isAnimationActive={false} />
                <Line dataKey="messageReactions" name="On messages" stroke={SERIES[2]} strokeWidth={2} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Sent outside playback: {data.unpositioned.messages} messages, {data.unpositioned.quickReactions} quick
              reactions, {data.unpositioned.messageReactions} message reactions.
            </Typography>
          </>
        );
      }}
    </StatsGate>
  );
}

export default function GameTimelineDrawer({ game, reloadKey, onClose }: {
  game: { tenant: string; gameId: string } | null; reloadKey: number; onClose: () => void;
}) {
  // Keeps the closing game on screen through the close animation: `game`
  // goes null the moment the drawer starts sliding away, and clearing the
  // body with it would leave the drawer visibly empty mid-slide.
  const [shown, setShown] = useState<{ tenant: string; gameId: string } | null>(null);
  if (game && game !== shown) setShown(game);

  return (
    <Drawer
      anchor="right" open={game !== null} onClose={onClose}
      SlideProps={{ onExited: () => setShown(null) }}
    >
      <Box sx={{ width: { xs: "100vw", md: 720 }, p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {shown ? `Game ${shown.gameId} (${shown.tenant})` : ""}
          </Typography>
          <IconButton onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
        </Box>
        {shown && <TimelineBody tenant={shown.tenant} gameId={shown.gameId} reloadKey={reloadKey} />}
      </Box>
    </Drawer>
  );
}
